import React from 'react';
import styled from 'styled-components';
import useSessionTimeline from '../hooks/useSessionTimeline';
import { Segment } from '../models/timeline';
import { Chip, FONT_SIZE_SCALE, PrintColors } from './PrintElements';
import { Grid, Type } from '../../../app/StyleGuide';

const SegmentGroupIconMap = Object.freeze({
  leaders: 'fas fa-chalkboard-teacher',
  small: 'fas fa-user-friends',
  large: 'fas fa-users',
});

const AgendaHeaderGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  gap: ${Grid._3};
  align-items: flex-end;
  margin-bottom: ${Grid._3};
`;

const AgendaTitle = styled.h2<{ fontSize: keyof typeof FONT_SIZE_SCALE }>`
  margin: 0;
  text-align: center;
  color: ${PrintColors.Green};
  line-height: 20px;
  font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].h2};
`;

const AgendaCustomizationMessage = styled.p<{ fontSize: keyof typeof FONT_SIZE_SCALE }>`
  font-style: italic;
  font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].body};
`;

const AgendaTimeText = styled.p<{ fontSize: keyof typeof FONT_SIZE_SCALE }>`
  text-align: center;
  align-self: flex-start;
  font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].base};
`;

const AgendaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 3fr;
  gap: ${Grid._3};
  align-items: flex-start;
  margin-bottom: ${Grid._3};
`;

const GroupHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${Grid._3};
`;

const GroupIcon = styled.i<{ fontSize: keyof typeof FONT_SIZE_SCALE }>`
  font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].h4};
`;

const GroupTitle = styled.h5<{ fontSize: keyof typeof FONT_SIZE_SCALE }>`
  font-weight: ${Type.Weight.medium};
  margin: 0;
  font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].h5};
`;

const DurationText = styled.p<{ fontSize: keyof typeof FONT_SIZE_SCALE }>`
  text-align: center;
  align-self: flex-start;
  white-space: nowrap;
  font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].base};
`;

const SegmentDetailsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Grid._3};
  margin-left: 30px;
`;

const SegmentTitle = styled.h5<{ fontSize: keyof typeof FONT_SIZE_SCALE }>`
  font-weight: ${Type.Weight.medium};
  font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].h5};
`;

const SegmentNameBold = styled.span`
  font-weight: ${Type.Weight.bold};
`;

const LeaderText = styled.span<{ fontSize: keyof typeof FONT_SIZE_SCALE }>`
  font-style: italic;
  font-weight: ${Type.Weight.normal};
  color: gray;
  font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].small};
`;

const showGroupHeader = (currentGroup: string, i: number, segments: Segment[]) => {
  if (i === 0) return true; // First segment always shows header
  return currentGroup !== segments[i - 1].group;
};

export default function Agenda({
  segments,
  fontSize,
}: {
  segments: Segment[];
  fontSize: keyof typeof FONT_SIZE_SCALE;
}) {
  const { segmentGroupMap, segmentLeaderMap } = useSessionTimeline();
  return (
    <>
      <AgendaHeaderGrid>
        <AgendaTitle fontSize={fontSize}>Agenda</AgendaTitle>
        <AgendaCustomizationMessage fontSize={fontSize}>
          <strong>This session has been customized</strong>
        </AgendaCustomizationMessage>
        <Chip color={PrintColors.Green} fontSize={fontSize}>
          <AgendaTimeText fontSize={fontSize}>
            {segments.reduce((acc, segment) => acc + segment.duration, 0)} Minutes
          </AgendaTimeText>
        </Chip>
      </AgendaHeaderGrid>
      <AgendaGrid>
        {segments?.map((segment, i) => {
          return (
            <React.Fragment key={segment.segmentId}>
              {showGroupHeader(segment.group, i, segments) && (
                <>
                  <div />
                  <GroupHeaderRow>
                    <GroupIcon
                      className={SegmentGroupIconMap[segment.group as keyof typeof SegmentGroupIconMap]}
                      fontSize={fontSize}
                    />
                    <GroupTitle fontSize={fontSize}>
                      {segmentGroupMap[segment.group as keyof typeof segmentGroupMap]}
                    </GroupTitle>
                  </GroupHeaderRow>
                </>
              )}

              <>
                {segment.duration > 0 ? (
                  <Chip color={PrintColors.LightGreen} fontSize={fontSize} style={{ margin: `0 ${Grid._2}` }}>
                    <DurationText fontSize={fontSize}>{segment.duration} minutes</DurationText>
                  </Chip>
                ) : (
                  <div />
                )}
                <div>
                  <SegmentDetailsContainer>
                    <SegmentTitle fontSize={fontSize}>
                      {i + 1}. <SegmentNameBold>{segment.name}</SegmentNameBold>
                    </SegmentTitle>
                    <LeaderText fontSize={fontSize}>
                      / {segmentLeaderMap[segment.leader as keyof typeof segmentLeaderMap]}
                    </LeaderText>
                  </SegmentDetailsContainer>
                </div>
              </>
            </React.Fragment>
          );
        })}
      </AgendaGrid>
    </>
  );
}
